import React, { useEffect, useState } from 'react';
import api from 'src/services/BaseService';
import Services from 'src/services/Services';
import { Helmet } from 'react-helmet-async';
import {
  Card,
  Stack,
  Typography,
  useMediaQuery,
  Select,
  MenuItem,
  Switch,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Button,
  Tabs,
  Tab,
  Box,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { toast } from 'react-toastify';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DescriptionIcon from '@mui/icons-material/Description';
import DynamicTable from 'src/components/shared/DynamicTable';
import { useLocalization } from 'src/context/LocalizationContext';

const StatusChip = ({ status }) => {
  const statusConfig = {
    Onaylandı: {
      label: 'Onaylandı',
      sx: {
        backgroundColor: '#EDEBFE',
        color: '#5521B5',
        fontWeight: 600,
        borderRadius: '4px',
      },
    },
    'Onay Bekliyor': {
      label: 'Onay Bekliyor',
      sx: {
        backgroundColor: '#FEF1EB',
        color: '#DC6525',
        fontWeight: 600,
        borderRadius: '4px',
      },
    },
    Reddedildi: {
      label: 'Reddedildi',
      sx: {
        backgroundColor: '#FDE8E8',
        color: '#9B1C1C',
        fontWeight: 600,
        borderRadius: '4px',
      },
    },
  };
  const config = statusConfig[status] || { label: status, color: 'default' };
  return <Chip label={config.label} sx={config.sx} size="small" />;
};

export default function StoreAuthorizations() {
  const { language } = useLocalization();
  const isMobile = useMediaQuery('(max-width:768px)');

  const [currentTab, setCurrentTab] = useState('yetkiTalepleri');
  const [branchList, setBranchList] = useState([]);

  const [userList, setUserList] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [managerDialog, setManagerDialog] = useState({
    open: false,
    type: 'confirm',
    user: null,
    loading: false,
  });

  const [allPermissionRequests, setAllPermissionRequests] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);

  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    branchId: '',
    status: '',
  });

  const [requestActionDialog, setRequestActionDialog] = useState({
    open: false,
    type: null,
    data: null,
    loading: false,
  });
  const [isTableLoading, setIsTableLoading] = useState(false);

  useEffect(() => {
    const mallID = localStorage.getItem('mallID');
    api.post('flow/get-malls-branches', { mallID }).then((response) => {
      if (response.data.status) {
        const resultData = response.data.result || [];
        let formattedBranches = resultData.map((item) => ({
          title: item.title,
          branchID: item.branchID,
        }));

        const storedUserStr = localStorage.getItem('user');
        if (storedUserStr) {
          try {
            const currentUser = JSON.parse(storedUserStr);

            if (
              currentUser.accountCategory === 'store' &&
              currentUser.branchID
            ) {
              formattedBranches = formattedBranches.filter(
                (b) => b.branchID == currentUser.branchID
              );

              setFilters((prev) => ({
                ...prev,
                branchId: currentUser.branchID,
              }));

              const matchedBranch = formattedBranches.find(
                (b) => b.branchID == currentUser.branchID
              );
              if (matchedBranch) {
                setSelectedBranch(matchedBranch);
              }
            }
          } catch (error) {
            console.error(error);
          }
        }

        setBranchList(formattedBranches);
      }
    });
  }, []);

  const handleBranchSelectForUsers = (branchID) => {
    const selected = branchList.find((branch) => branch.branchID === branchID);
    setSelectedBranch(selected);
    api.post('users/get-users', { branchID, status: true }).then((response) => {
      if (response.data.status) {
        setUserList(
          response.data.result.map((user) => ({
            ...user,
            branchTitle: selected?.title,
          }))
        );
      } else {
        setUserList([]);
      }
    });
  };

  const handleSwitchChange = (user) => {
    setManagerDialog({ open: true, type: 'confirm', user, loading: false });
  };

  const handleConfirmManagerAction = async () => {
    console.log('Manager Dialog:', managerDialog);
  };

  const closeManagerDialog = () => {
    setManagerDialog({
      open: false,
      type: 'confirm',
      user: null,
      loading: false,
    });
  };

  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp * 1000);
    return (
      date.toLocaleDateString('tr-TR') +
      ' ' +
      date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
    );
  };

  const fetchPermissionRequests = async () => {
    setIsTableLoading(true);
    try {
      const response = await Services.GetAdminRequestList();
      if (response.data && response.data.status) {
        const rawData = response.data.result || [];

        const processedData = rawData.map((req) => {
          const isManager = req.frontAuth && req.frontAuth.includes('manager');
          const status = isManager ? 'Onaylandı' : 'Onay Bekliyor';

          let reqBranchID = '';
          let reqBranchName = req.branchTitle || '-';

          const userBranchIds = req.authLocations?.branch || [];
          const matchedBranch = branchList.find((b) =>
            userBranchIds.includes(b.branchID)
          );

          if (matchedBranch) {
            reqBranchID = matchedBranch.branchID;
            reqBranchName = matchedBranch.title;
          }

          return {
            ...req,
            userID: req.id,
            requestDate: formatDateTime(req.requestAsAdminDateTime),
            status: status,
            branchID: reqBranchID,
            branchName: reqBranchName,
          };
        });

        const validRequests = processedData.filter(
          (req) => req.branchID !== ''
        );
        let finalRequests = validRequests;

        try {
          const storedUserStr = localStorage.getItem('user');
          if (storedUserStr) {
            const currentUser = JSON.parse(storedUserStr);
            if (currentUser.accountCategory === 'store') {
              finalRequests = validRequests.filter(
                (req) => req.branchID == currentUser.branchID
              );
            }
          }
        } catch (error) {
          console.error(error);
        }

        setAllPermissionRequests(finalRequests);
      }
    } catch (error) {
      console.error(error);
      toast.error('Liste alınamadı.');
    } finally {
      setIsTableLoading(false);
    }
  };

  useEffect(() => {
    if (currentTab === 'yetkiTalepleri' && branchList.length > 0) {
      fetchPermissionRequests();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab, branchList]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ startDate: null, endDate: null, branchId: '', status: '' });
  };

  useEffect(() => {
    let filteredData = allPermissionRequests;

    if (filters.status) {
      filteredData = filteredData.filter(
        (req) => req.status === filters.status
      );
    }
    if (filters.branchId) {
      filteredData = filteredData.filter(
        (req) => req.branchID === filters.branchId
      );
    }

    setPermissionRequests(filteredData);
  }, [filters, allPermissionRequests]);

  const handleRequestActionConfirm = async () => {
    setRequestActionDialog((prev) => ({ ...prev, loading: true }));
    const { type, data } = requestActionDialog;

    try {
      if (type === 'approve') {
        const userID = data.userID;
        const response = await Services.ConfirmAdminRequest(userID);

        if (response.data && response.data.status) {
          setAllPermissionRequests((prevRequests) =>
            prevRequests.map((req) =>
              req.userID === userID ? { ...req, status: 'Onaylandı' } : req
            )
          );

          setRequestActionDialog((prev) => ({
            ...prev,
            type: 'approve-success',
            loading: false,
          }));
        } else {
          toast.error(response.data?.message || 'İşlem başarısız.');
          setRequestActionDialog((prev) => ({ ...prev, loading: false }));
        }
      } else if (type === 'reject') {
        const userID = data.userID;
        const response = await Services.RejectAdminRequest(userID);

        if (response.data && response.data.status) {
          setAllPermissionRequests((prevRequests) =>
            prevRequests.map((req) =>
              req.userID === userID ? { ...req, status: 'Reddedildi' } : req
            )
          );

          setRequestActionDialog((prev) => ({
            ...prev,
            type: 'reject-success',
            loading: false,
          }));
        } else {
          toast.error(response.data?.message || 'Reddetme işlemi başarısız.');
          setRequestActionDialog((prev) => ({ ...prev, loading: false }));
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Hata oluştu.');
      setRequestActionDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const closeRequestDialog = () => {
    setRequestActionDialog({
      open: false,
      type: null,
      data: null,
      loading: false,
    });
  };

  const TABLE_HEAD_OLD = [
    { id: 'username', label: language.commons.labels.username },
    { id: 'name', label: language.commons.labels.name },
    { id: 'lastname', label: language.commons.labels.lastname },
    {
      id: 'frontAuth',
      label: language.commons.labels.frontAuth,
      format: (value) =>
        Array.isArray(value)
          ? value
              .map(
                (val) =>
                  ({ operations: 'Operasyon', manager: 'Yönetici' }[val] || val)
              )
              .join(', ')
          : '',
    },
    { id: 'branchTitle', label: language.commons.column.store_name },
  ];

  // GÜNCELLEME: TELEFON sütunu eklendi
  const TABLE_HEAD_NEW = [
    { id: 'no', label: 'NO', format: (value, row, index) => index + 1 },
    { id: 'username', label: 'KULLANICI ADI' },
    { id: 'name', label: 'AD' },
    { id: 'lastname', label: 'SOYAD' },
    { id: 'branchName', label: 'MAĞAZA ADI' },
    { id: 'email', label: 'E-POSTA ADRESİ' },
    { id: 'phone', label: 'TELEFON' }, // Yeni sütun
    { id: 'requestDate', label: 'TALEP TARİHİ' },
    {
      id: 'status',
      label: 'DURUMU',
      format: (value) => <StatusChip status={value} />,
    },
    {
      id: 'description',
      label: 'AÇIKLAMA',
      alignRight: true,
      format: (value, row) => (
        <Tooltip title="Açıklamayı Gör">
          <IconButton
            onClick={() =>
              setRequestActionDialog({ open: true, type: 'view', data: row })
            }
          >
            <DescriptionIcon />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      id: 'actions',
      label: 'TALEP ONAYI',
      alignRight: true,
      format: (value, row) =>
        row.status === 'Onay Bekliyor' && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              size="small"
              onClick={() =>
                setRequestActionDialog({
                  open: true,
                  type: 'approve',
                  data: row,
                })
              }
            >
              Onayla
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              onClick={() =>
                setRequestActionDialog({
                  open: true,
                  type: 'reject',
                  data: row,
                })
              }
            >
              Reddet
            </Button>
          </Stack>
        ),
    },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Helmet>
        <title>Mağaza İzinleri</title>
      </Helmet>

      <Stack sx={{ p: { xs: 2, md: 3 } }}>
        <Stack sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Mağaza İzinleri
          </Typography>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            TabIndicatorProps={{ sx: { display: 'none' } }}
            sx={{
              width: '100%',
              maxWidth: '400px',
              backgroundColor: '#DBDBDB',
              borderRadius: '12px',
              p: 0.5,
              mx: 'auto',
            }}
          >
            <Tab
              label="Mevcut İzinler"
              value="mevcutIzinler"
              disableRipple
              sx={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                '&.Mui-selected': {
                  backgroundColor: '#fff',
                  color: 'text.primary',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                },
                color: 'text.secondary',
              }}
            />
            <Tab
              label="Yetki Talepleri"
              value="yetkiTalepleri"
              disableRipple
              sx={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '10px',
                '&.Mui-selected': {
                  backgroundColor: '#fff',
                  color: 'text.primary',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                },
                color: 'text.secondary',
              }}
            />
          </Tabs>
        </Stack>

        {currentTab === 'mevcutIzinler' && (
          <Card>
            <DynamicTable
              data={userList}
              headers={TABLE_HEAD_OLD}
              isMobile={isMobile}
            >
              <Box sx={{ p: 2 }}>
                <Select
                  sx={{ width: '250px' }}
                  value={selectedBranch?.branchID || ''}
                  onChange={(e) => handleBranchSelectForUsers(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {language.commons.select_store}
                  </MenuItem>
                  {branchList.map((branch) => (
                    <MenuItem key={branch.branchID} value={branch.branchID}>
                      {branch.title}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </DynamicTable>
          </Card>
        )}

        {currentTab === 'yetkiTalepleri' && (
          <Card sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Başlangıç Tarihi"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2.5}>
                <DatePicker
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Bitiş Tarihi"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Select
                  fullWidth
                  value={filters.branchId}
                  onChange={(e) =>
                    handleFilterChange('branchId', e.target.value)
                  }
                  displayEmpty
                  size="small"
                  sx={{ height: '54px' }}
                >
                  <MenuItem value="">
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: 'inherit' }}
                    >
                      Mağaza Seçiniz
                    </Typography>
                  </MenuItem>
                  {branchList.map((branch) => (
                    <MenuItem key={branch.branchID} value={branch.branchID}>
                      {branch.title}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                container
                spacing={1}
                justifyContent="flex-end"
              >
                <Grid item>
                  <Button variant="outlined" onClick={handleClearFilters}>
                    TEMİZLE
                  </Button>
                </Grid>
                <Grid item>
                  <Button variant="contained" onClick={fetchPermissionRequests}>
                    LİSTELE
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={1} sx={{ pt: 1 }}>
              <Typography
                variant="subtitle2"
                sx={{ mr: 1, alignSelf: 'center' }}
              >
                Teslim Durumu:
              </Typography>
              <Chip
                icon={
                  <FiberManualRecordIcon
                    sx={{ fontSize: 14, color: '#4318FF !important' }}
                  />
                }
                label="Onaylandı"
                onClick={() => handleFilterChange('status', 'Onaylandı')}
                sx={{
                  bgcolor: '#F1F1FF',
                  color: '#4318FF',
                  fontWeight: 600,
                  borderRadius: '4px',
                  border:
                    filters.status === 'Onaylandı'
                      ? '1px solid #4318FF'
                      : '1px solid transparent',
                }}
              />
              <Chip
                icon={
                  <FiberManualRecordIcon
                    sx={{ fontSize: 14, color: '#FF8A00 !important' }}
                  />
                }
                label="Onay Bekliyor"
                onClick={() => handleFilterChange('status', 'Onay Bekliyor')}
                sx={{
                  bgcolor: '#FFF4E5',
                  color: '#FF8A00',
                  fontWeight: 600,
                  borderRadius: '4px',
                  border:
                    filters.status === 'Onay Bekliyor'
                      ? '1px solid #FF8A00'
                      : '1px solid transparent',
                }}
              />
              <Chip
                icon={
                  <FiberManualRecordIcon
                    sx={{ fontSize: 14, color: '#DC3545 !important' }}
                  />
                }
                label="Reddedildi"
                onClick={() => handleFilterChange('status', 'Reddedildi')}
                sx={{
                  bgcolor: '#FFECEC',
                  color: '#DC3545',
                  fontWeight: 600,
                  borderRadius: '4px',
                  border:
                    filters.status === 'Reddedildi'
                      ? '1px solid #DC3545'
                      : '1px solid transparent',
                }}
              />
            </Stack>
            <DynamicTable
              data={permissionRequests}
              headers={TABLE_HEAD_NEW}
              isLoading={isTableLoading}
              isMobile={isMobile}
            />
          </Card>
        )}
      </Stack>

      <Dialog
        open={managerDialog.open}
        onClose={closeManagerDialog}
        maxWidth="xs"
        fullWidth
      >
        {managerDialog.type === 'confirm' ? (
          <>
            <DialogTitle>Onay Talebi</DialogTitle>
            <DialogContent>
              <DialogContentText>
                <b>{`${managerDialog.user?.name} ${managerDialog.user?.lastname}`}</b>{' '}
                adlı kullanıcıyı{' '}
                {managerDialog.user?.frontAuth?.includes('manager')
                  ? 'yönetici yetkisinden almayı'
                  : 'yönetici olarak atamayı'}{' '}
                onaylıyor musunuz?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeManagerDialog}>Kapat</Button>
              <Button
                onClick={handleConfirmManagerAction}
                variant="contained"
                disabled={managerDialog.loading}
              >
                {managerDialog.loading ? (
                  <CircularProgress size={24} />
                ) : (
                  'Onayla'
                )}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>İşlem Başarılı</DialogTitle>
            <DialogContent>
              <DialogContentText>İşlem başarıyla tamamlandı.</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeManagerDialog}>Kapat</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={requestActionDialog.open}
        onClose={closeRequestDialog}
        maxWidth="xs"
        fullWidth
      >
        {(requestActionDialog.type === 'approve' ||
          requestActionDialog.type === 'reject') && (
          <>
            <DialogTitle>
              {requestActionDialog.type === 'approve'
                ? 'Talebi Onayla'
                : 'Talebi Reddet'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <b>{`"${requestActionDialog.data?.name} ${requestActionDialog.data?.lastname}"`}</b>{' '}
                adlı kullanıcının talebini{' '}
                {requestActionDialog.type === 'approve'
                  ? 'onaylamak'
                  : 'reddetmek'}{' '}
                istediğinize emin misiniz?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRequestDialog}>Vazgeç</Button>
              <Button
                onClick={handleRequestActionConfirm}
                variant="contained"
                color={
                  requestActionDialog.type === 'approve' ? 'primary' : 'error'
                }
                disabled={requestActionDialog.loading}
              >
                {requestActionDialog.loading ? (
                  <CircularProgress size={24} />
                ) : requestActionDialog.type === 'approve' ? (
                  'Onayla'
                ) : (
                  'Reddet'
                )}
              </Button>
            </DialogActions>
          </>
        )}
        {requestActionDialog.type === 'view' && (
          <>
            <DialogTitle>Talep Açıklaması</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {requestActionDialog.data?.requestAsAdminDescription}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRequestDialog}>Kapat</Button>
            </DialogActions>
          </>
        )}
        {requestActionDialog.type === 'approve-success' && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              İşlem Başarılı
            </Typography>
            <Typography>
              <b>{`${requestActionDialog.data?.name} ${requestActionDialog.data?.lastname}`}</b>{' '}
              isimli mağaza personeline yöneticilik yetkisi tanımlanmıştır.
              Konuya ilişkin kurumsal e-posta ile bilgilendirme yapılacaktır.
            </Typography>
            <Button
              onClick={closeRequestDialog}
              variant="contained"
              sx={{ mt: 3 }}
            >
              Kapat
            </Button>
          </Box>
        )}
        {requestActionDialog.type === 'reject-success' && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <CancelIcon color="error" sx={{ fontSize: 64, mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              İşlem Reddedildi
            </Typography>
            <Typography>
              <b>{`${requestActionDialog.data?.name} ${requestActionDialog.data?.lastname}`}</b>{' '}
              isimli mağaza personelinin yöneticilik yetkisi reddedilmiştir.
              Konuya ilişkin kurumsal e-posta ile bilgilendirme yapılacaktır.
            </Typography>
            <Button
              onClick={closeRequestDialog}
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
            >
              Kapat
            </Button>
          </Box>
        )}
      </Dialog>
    </LocalizationProvider>
  );
}
